import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useDashboardWs } from "../context/DashboardContext";
import { isUiLoadReduced, subscribeUiLoad } from "../lib/uiLoadStore";

export type ChargerLogSource = "ocpp" | "ws";

export type ChargerLogResult = {
  charger_id: string;
  source: ChargerLogSource;
  filePath?: string;
  lines: string[];
  truncated?: boolean;
  file_size?: number;
  scanned_bytes?: number;
  limit?: number;
  can_load_more?: boolean;
};

type LogState = {
  data: ChargerLogResult | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
};

type FetchMode = "initial" | "poll" | "more";

export const CHARGER_LOG_INITIAL_LIMIT = 200;
export const CHARGER_LOG_LOAD_MORE_STEP = 200;

export function useChargerLog(
  chargerId: string | null,
  source: ChargerLogSource | null,
  paused: boolean,
  pollMs = 1000,
) {
  const { requestChargerLog, wsReady } = useDashboardWs();
  const loadReduced = useSyncExternalStore(subscribeUiLoad, isUiLoadReduced, isUiLoadReduced);
  const effectivePaused = paused || loadReduced;
  const enabled = Boolean(chargerId && source);
  const [limit, setLimit] = useState(CHARGER_LOG_INITIAL_LIMIT);
  const [state, setState] = useState<LogState>({
    data: null,
    loading: false,
    loadingMore: false,
    error: null,
  });
  const inFlightRef = useRef(false);
  const fetchIdRef = useRef(0);
  const requestChargerLogRef = useRef(requestChargerLog);
  requestChargerLogRef.current = requestChargerLog;

  const fetchLog = useCallback(
    async (lineLimit: number, mode: FetchMode = "initial") => {
      if (!chargerId || !source) return;
      if (mode === "poll" && inFlightRef.current) return;

      const id = ++fetchIdRef.current;
      inFlightRef.current = true;
      setState((s) => ({
        ...s,
        loading: mode === "initial" && !s.data,
        loadingMore: mode === "more",
        error: null,
      }));
      try {
        const data = await requestChargerLogRef.current(chargerId, source, lineLimit);
        if (id !== fetchIdRef.current) return;
        setState({ data, loading: false, loadingMore: false, error: null });
      } catch (err) {
        if (id !== fetchIdRef.current) return;
        setState((s) => ({
          data: s.data,
          loading: false,
          loadingMore: false,
          error: s.data
            ? null
            : err instanceof Error
              ? err.message
              : "Failed to load log",
        }));
      } finally {
        if (id === fetchIdRef.current) inFlightRef.current = false;
      }
    },
    [chargerId, source],
  );

  const loadMore = useCallback(() => {
    const next = limit + CHARGER_LOG_LOAD_MORE_STEP;
    setLimit(next);
    void fetchLog(next, "more");
  }, [limit, fetchLog]);

  useEffect(() => {
    if (!enabled) {
      fetchIdRef.current += 1;
      setLimit(CHARGER_LOG_INITIAL_LIMIT);
      setState({ data: null, loading: false, loadingMore: false, error: null });
      return;
    }
    setLimit(CHARGER_LOG_INITIAL_LIMIT);
    if (!wsReady) return;
    void fetchLog(CHARGER_LOG_INITIAL_LIMIT, "initial");
  }, [enabled, chargerId, source, fetchLog, wsReady]);

  useEffect(() => {
    if (!enabled || !wsReady || effectivePaused) return;
    const id = window.setInterval(() => void fetchLog(limit, "poll"), pollMs);
    return () => window.clearInterval(id);
  }, [enabled, wsReady, effectivePaused, pollMs, limit, fetchLog]);

  return {
    ...state,
    limit,
    reload: () => fetchLog(limit, "initial"),
    loadMore,
  };
}
